/**
 * @fileoverview Property Onboarding Component for Dashboard
 * @module PropertyOnboarding
 * 
 * Business Context:
 * - Displayed on dashboard when user has no property associated with account
 * - Collects essential property information for personalization
 * - Used when users skip the funnel (direct CTA access) or if property save failed
 * - Auto-populates property data from Texas parcel database when available
 * 
 * Technical Context:
 * - Reuses AddressSearch component for address autocomplete
 * - Integrates with Texas parcel database for auto-population
 * - Uses CountySelector for county selection (fallback)
 * - Saves property via apiClient.users.addProperty()
 * - Calls onComplete callback when property is successfully added
 */

import React, { useState, useCallback } from 'react';
import { 
  Home, 
  MapPin, 
  ArrowRight, 
  AlertCircle, 
  CheckCircle,
  Building2,
  Loader2,
  Sparkles,
  Calendar,
  DollarSign,
  Edit3
} from 'lucide-react';
import { AddressSearch, AddressData } from '../home/AddressSearch';
import { CountySelector } from '../property/CountySelector';
import { apiClient } from '../../api/client';

// Property type options
const PROPERTY_TYPES = [
  { id: 'single_family', label: 'Single Family Home', icon: Home },
  { id: 'condo', label: 'Condo / Apartment', icon: Building2 },
  { id: 'townhouse', label: 'Townhouse', icon: Building2 },
  { id: 'mobile_home', label: 'Mobile Home', icon: Home },
] as const;

type PropertyType = typeof PROPERTY_TYPES[number]['id'];

// Texas Comptroller Property Classification Codes
// Maps state land use codes to our property types
const LAND_USE_TO_PROPERTY_TYPE: Record<string, PropertyType> = {
  'A': 'single_family',   // Single-family residential
  'A1': 'single_family',  // Some CADs use subcodes
  'A2': 'single_family',
  'B': 'condo',           // Multifamily residential (condos/apartments)
  'B1': 'condo',
  'B2': 'townhouse',      // Townhouse classification
  'M': 'mobile_home',     // Mobile homes
  'MH': 'mobile_home',
};

/**
 * Maps Texas state land use code to our property type
 */
function mapLandUseToPropertyType(code: string | null): PropertyType {
  if (!code) return 'single_family'; // Default
  
  const upperCode = code.toUpperCase();
  // Try exact match first, then first character
  return LAND_USE_TO_PROPERTY_TYPE[upperCode] 
    || LAND_USE_TO_PROPERTY_TYPE[upperCode.charAt(0)] 
    || 'single_family';
}

/**
 * Gets the display label for a property type
 */
function getPropertyTypeLabel(type: PropertyType): string {
  const found = PROPERTY_TYPES.find(t => t.id === type);
  return found?.label || 'Single Family Home';
}

/**
 * Formats currency for display
 */
function formatCurrency(valueInDollars: number | null): string {
  if (valueInDollars === null || valueInDollars === undefined) return 'Not available';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(valueInDollars);
}

// Parcel data interface (from Texas parcel database)
interface ParcelBasicInfo {
  propId: string;
  county: string;
  fips: string;
  situsAddr: string | null;
  situsCity: string | null;
  situsZip: string | null;
  mktValue: number | null;     // In cents
  landValue: number | null;    // In cents
  impValue: number | null;     // In cents
  taxYear: number | null;
  yearBuilt: number | null;
  legalArea: number | null;
  legalAreaUnit: string | null;
  landUseCode: string | null;
}

interface County {
  id: number;
  name: string;
  state: string;
}

interface PropertyOnboardingProps {
  onComplete: () => void;
  userName?: string | undefined;
}

export const PropertyOnboarding: React.FC<PropertyOnboardingProps> = ({
  onComplete,
  userName
}) => {
  // Form state - now includes 'loading' and 'confirm' steps
  const [step, setStep] = useState<'address' | 'loading' | 'confirm' | 'details'>('address');
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [countyId, setCountyId] = useState<number | null>(null);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  const [propertyType, setPropertyType] = useState<PropertyType>('single_family');
  const [isHomestead, setIsHomestead] = useState(true);
  
  // Parcel data state
  const [parcelData, setParcelData] = useState<ParcelBasicInfo | null>(null);
  const [parcelConfidence, setParcelConfidence] = useState<number>(0);
  const [yearBuilt, setYearBuilt] = useState<number | null>(null);
  const [assessedValue, setAssessedValue] = useState<number | null>(null); // In dollars
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Maps county name from parcel data to our internal county ID
   */
  const mapCountyNameToId = useCallback(async (countyName: string): Promise<{ id: number; county: County } | null> => {
    try {
      const response = await apiClient.properties.searchCounties(countyName);
      const counties = response.data?.data || [];
      const match = counties.find(
        (c: { name: string }) => c.name.toLowerCase() === countyName.toLowerCase()
      );
      if (match) {
        return { id: match.id, county: match };
      }
      return null;
    } catch (err) {
      console.warn('Could not lookup county:', err);
      return null;
    }
  }, []);

  // Handle address selection from autocomplete - now includes parcel lookup
  const handleAddressSelect = useCallback(async (address: AddressData) => {
    setAddressData(address);
    setError(null);
    setStep('loading'); // Show loading state while looking up parcel
    
    try {
      // Lookup parcel from Texas database
      const response = await apiClient.parcels.lookup(
        address.streetAddress,
        address.city,
        address.zip
      );
      
      const parcelResult = response.data;
      
      if (parcelResult?.found && parcelResult.confidence > 0.5) {
        const parcel = parcelResult.parcel as ParcelBasicInfo;
        
        console.log('Found parcel:', parcel);
        
        // Store parcel data
        setParcelData(parcel);
        setParcelConfidence(parcelResult.confidence);
        
        // Auto-populate from parcel data
        const mappedPropertyType = mapLandUseToPropertyType(parcel.landUseCode);
        setPropertyType(mappedPropertyType);
        
        if (parcel.yearBuilt) {
          setYearBuilt(parcel.yearBuilt);
        }
        
        // Convert market value from cents to dollars
        if (parcel.mktValue) {
          setAssessedValue(parcel.mktValue / 100);
        }
        
        // Map county name to our county ID
        if (parcel.county) {
          const countyResult = await mapCountyNameToId(parcel.county);
          if (countyResult) {
            setCountyId(countyResult.id);
            setSelectedCounty(countyResult.county);
          }
        }
        
        // Show confirmation step with high confidence, details with lower
        if (parcelResult.confidence > 0.7) {
          setStep('confirm');
        } else {
          // Medium confidence - go to details but pre-fill values
          setStep('details');
        }
      } else {
        // Parcel not found - fall back to manual entry
        console.log('Parcel not found, falling back to manual entry');
        
        // Still try to lookup county from Google Places data
        if (address.county) {
          const countyResult = await mapCountyNameToId(address.county);
          if (countyResult) {
            setCountyId(countyResult.id);
            setSelectedCounty(countyResult.county);
          }
        }
        
        setStep('details');
      }
    } catch (err) {
      console.warn('Parcel lookup failed, falling back to manual entry:', err);
      
      // Graceful fallback - try county lookup from Google Places data
      if (address.county) {
        try {
          const countyResult = await mapCountyNameToId(address.county);
          if (countyResult) {
            setCountyId(countyResult.id);
            setSelectedCounty(countyResult.county);
          }
        } catch {
          // Ignore county lookup failure
        }
      }
      
      setStep('details');
    }
  }, [mapCountyNameToId]);

  // Handle county selection (for manual mode)
  const handleCountyChange = useCallback((id: number | null, county: County | null) => {
    setCountyId(id);
    setSelectedCounty(county);
    setError(null);
  }, []);

  // Handle form submission
  const handleSubmit = async () => {
    if (!addressData) {
      setError('Please enter your property address.');
      setStep('address');
      return;
    }
    
    if (!countyId) {
      setError('Please select your county.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const propertyData: Record<string, unknown> = {
        addressLine1: addressData.streetAddress,
        city: addressData.city || parcelData?.situsCity || 'Unknown',
        zipCode: addressData.zip || parcelData?.situsZip || '00000',
        countyId: countyId,
        propertyType: propertyType,
        homesteadStatus: isHomestead,
      };

      // Include parcel-derived data if available
      if (yearBuilt) {
        propertyData.yearBuilt = yearBuilt;
      }
      if (assessedValue) {
        // Convert dollars back to cents for storage
        propertyData.assessedValue = Math.round(assessedValue * 100);
      }
      if (parcelData?.propId) {
        propertyData.parcelId = parcelData.propId;
      }

      console.log('💾 Saving property:', propertyData);
      const response = await apiClient.users.addProperty(propertyData);
      
      if (response.data.success) {
        console.log('✅ Property saved successfully');
        onComplete();
      } else {
        throw new Error(response.data.error?.message || 'Failed to save property');
      }
    } catch (err) {
      console.error('Error saving property:', err);
      setError(err instanceof Error ? err.message : 'Failed to save property. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Switch from confirm to manual details editing
  const handleEditDetails = () => {
    setStep('details');
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 md:py-12" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="w-full max-w-2xl">
        {/* Unified Card with integrated header */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Compact Header Banner */}
          <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-5 sm:px-8 sm:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/80 text-xs font-medium uppercase tracking-wider">
                    {userName ? `Welcome, ${userName}!` : 'Welcome!'}
                  </p>
                  <h1 className="text-white text-lg sm:text-xl font-bold">
                    {step === 'address' ? "Let's Get Started" : 
                     step === 'confirm' ? 'Confirm Your Property' : 
                     step === 'details' ? 'Property Details' : 'Finding Your Property'}
                  </h1>
                </div>
              </div>
              
              {/* Inline Step Indicator */}
              {step !== 'loading' && (
                <div className="hidden sm:flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step === 'address' ? 'bg-white text-teal-600' : 'bg-white/30 text-white'
                  }`}>
                    {addressData ? <CheckCircle className="w-4 h-4" /> : '1'}
                  </div>
                  <div className="w-8 h-0.5 bg-white/30 rounded-full overflow-hidden">
                    <div className={`h-full bg-white transition-all duration-500 ${addressData ? 'w-full' : 'w-0'}`} />
                  </div>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    (step === 'confirm' || step === 'details') ? 'bg-white text-teal-600' : 'bg-white/30 text-white'
                  }`}>
                    2
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Content Area */}
          <div className="p-6 sm:p-8">
            {/* Mobile Step Indicator */}
            {step !== 'loading' && (
              <div className="flex sm:hidden items-center justify-center gap-3 mb-6">
                <div className={`flex items-center gap-2 ${step === 'address' ? 'text-teal-600' : 'text-gray-400'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step === 'address' ? 'bg-teal-500 text-white' : 
                    addressData ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {addressData ? <CheckCircle className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="font-medium text-sm">Address</span>
                </div>
                <div className="w-12 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full bg-teal-500 transition-all duration-500 ${addressData ? 'w-full' : 'w-0'}`} />
                </div>
                <div className={`flex items-center gap-2 ${(step === 'confirm' || step === 'details') ? 'text-teal-600' : 'text-gray-400'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    (step === 'confirm' || step === 'details') ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-400'
                  }`}>
                    2
                  </div>
                  <span className="font-medium text-sm">{step === 'confirm' ? 'Confirm' : 'Details'}</span>
                </div>
              </div>
            )}

          {/* Step 1: Address */}
          {step === 'address' && (
            <div className="space-y-5">
              <div className="text-center">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  What's your property address?
                </h2>
                <p className="text-gray-500 text-sm sm:text-base">
                  Enter your Texas property address to get personalized content.
                </p>
              </div>

              <AddressSearch
                onAddressSelect={handleAddressSelect}
                placeholder="Enter your Texas property address"
                buttonText="Continue"
                onButtonClick={() => {
                  if (addressData) {
                    setStep('details');
                  }
                }}
              />
              
              <p className="text-center text-xs text-gray-400">
                e.g., 123 Main St, Houston, TX 77001
              </p>
            </div>
          )}

          {/* Loading State: Looking up parcel data */}
          {step === 'loading' && (
            <div className="text-center py-10">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-50 rounded-full mb-4">
                <Loader2 className="w-7 h-7 animate-spin text-teal-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Looking Up Your Property
              </h2>
              <p className="text-gray-500 text-sm max-w-sm mx-auto">
                Searching the Texas property database...
              </p>
            </div>
          )}

          {/* Confirmation Step: Show auto-populated parcel data */}
          {step === 'confirm' && parcelData && (
            <div className="space-y-5">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                  We Found Your Property!
                </h2>
                <p className="text-gray-500 text-sm">
                  Please confirm these details are correct.
                </p>
              </div>

              {/* Property Card with auto-populated data */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-4 sm:p-5 border border-teal-100">
                {/* Address - full width */}
                <div className="flex items-start gap-3 pb-3 mb-3 border-b border-teal-200/50">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-teal-600 uppercase tracking-wide">Property Address</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{addressData?.fullAddress || parcelData.situsAddr}</p>
                  </div>
                </div>

                {/* 2x2 Grid of details */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">County</p>
                      <p className="font-medium text-gray-900 truncate">{parcelData.county}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="font-medium text-gray-900 truncate">{getPropertyTypeLabel(propertyType)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Year Built</p>
                      <p className="font-medium text-gray-900">{yearBuilt || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Assessed</p>
                      <p className="font-medium text-gray-900">{assessedValue ? formatCurrency(assessedValue) : '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Edit button */}
                <div className="mt-3 pt-3 border-t border-teal-200/50">
                  <button
                    type="button"
                    onClick={handleEditDetails}
                    className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 font-medium text-xs"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit details
                  </button>
                </div>
              </div>

              {/* Homestead Question */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Is this your primary residence?
                </label>
                <div className="flex items-stretch gap-2">
                  <button
                    type="button"
                    onClick={() => setIsHomestead(true)}
                    className={`
                      flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all duration-200 text-center text-sm
                      ${isHomestead 
                        ? 'border-teal-500 bg-teal-50 text-teal-700' 
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    Yes, primary residence
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsHomestead(false)}
                    className={`
                      flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all duration-200 text-center text-sm
                      ${!isHomestead 
                        ? 'border-teal-500 bg-teal-50 text-teal-700' 
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* Confirm Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !countyId}
                className={`
                  w-full flex justify-center items-center gap-2 py-3.5 px-6 rounded-xl
                  font-semibold transition-all duration-200
                  ${isSubmitting || !countyId
                    ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                    : 'bg-teal-500 hover:bg-teal-600 text-white shadow-md hover:shadow-lg'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Confirm & Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Property Details (Manual Entry / Edit Mode) */}
          {step === 'details' && (
            <div className="space-y-5">
              <div className="text-center mb-2">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                  Property Details
                </h2>
                <p className="text-gray-500 text-sm">
                  {parcelData 
                    ? 'Review and edit your property details.'
                    : 'Tell us a bit more about your property.'}
                </p>
              </div>

              {/* Selected Address Display */}
              {addressData && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-teal-900 text-sm truncate">{addressData.fullAddress}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setStep('address');
                          setParcelData(null);
                          setYearBuilt(null);
                          setAssessedValue(null);
                        }}
                        className="text-teal-600 text-xs font-medium hover:underline"
                      >
                        Change address
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* County Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Texas County <span className="text-red-500">*</span>
                </label>
                <CountySelector
                  value={countyId}
                  onChange={handleCountyChange}
                  placeholder="Select your county"
                  required
                  searchable
                  className="w-full"
                />
              </div>

              {/* Property Type Selection - Compact 2x2 grid */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Property Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PROPERTY_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = propertyType === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setPropertyType(type.id)}
                        className={`
                          p-3 rounded-xl border-2 text-left transition-all duration-200
                          ${isSelected 
                            ? 'border-teal-500 bg-teal-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            isSelected ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className={`font-medium text-sm ${isSelected ? 'text-teal-900' : 'text-gray-700'}`}>
                            {type.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Year Built & Assessed Value - compact fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Year Built <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    value={yearBuilt || ''}
                    onChange={(e) => setYearBuilt(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="e.g., 1998"
                    min="1800"
                    max={new Date().getFullYear()}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Assessed Value <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="text"
                      value={assessedValue ? assessedValue.toLocaleString() : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setAssessedValue(value ? parseInt(value) : null);
                      }}
                      placeholder="350,000"
                      className="w-full pl-7 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Homestead Exemption - Compact */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Primary Residence?
                </label>
                <div className="flex items-stretch gap-2">
                  <button
                    type="button"
                    onClick={() => setIsHomestead(true)}
                    className={`
                      flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all duration-200 text-center text-sm
                      ${isHomestead 
                        ? 'border-teal-500 bg-teal-50 text-teal-700' 
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    Yes (Homestead)
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsHomestead(false)}
                    className={`
                      flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all duration-200 text-center text-sm
                      ${!isHomestead 
                        ? 'border-teal-500 bg-teal-50 text-teal-700' 
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !countyId}
                className={`
                  w-full flex justify-center items-center gap-2 py-3.5 px-6 rounded-xl
                  font-semibold transition-all duration-200
                  ${isSubmitting || !countyId
                    ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                    : 'bg-teal-500 hover:bg-teal-600 text-white shadow-md hover:shadow-lg'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Start My Appeal Journey
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyOnboarding;
